(() => {
  const elementName = 'cuero-campaign-carousel';
  if (customElements.get(elementName)) return;

  class CueroCampaignCarousel extends HTMLElement {
    connectedCallback() {
      if (this.cleanupController) return;

      this.cleanupController = new AbortController();
      this.panels = Array.from(this.querySelectorAll('[data-cuc-panel]'));
      this.selectors = Array.from(this.querySelectorAll('[data-cuc-select]'));
      this.backControl = this.querySelector('[data-cuc-back]');
      this.forwardControl = this.querySelector('[data-cuc-forward]');
      this.pauseControl = this.querySelector('[data-cuc-pause]');
      this.activeIndex = Math.max(0, this.panels.findIndex((panel) => panel.hasAttribute('data-active')));
      this.interval = Math.max(3000, Number.parseInt(this.dataset.interval || '5000', 10));
      this.shouldCycle = this.dataset.cycle === 'true' && this.panels.length > 1;
      this.depthEnabled = this.dataset.depth === 'true';
      this.pausedByUser = false;
      this.pointerOrigin = null;
      this.timeoutId = 0;
      this.frameId = 0;

      const signal = this.cleanupController.signal;
      this.backControl?.addEventListener('click', () => this.move(-1), { signal });
      this.forwardControl?.addEventListener('click', () => this.move(1), { signal });
      this.pauseControl?.addEventListener('click', () => this.togglePlayback(), { signal });

      this.selectors.forEach((selector) => {
        selector.addEventListener('click', () => this.activate(Number(selector.dataset.cucSelect), true), { signal });
      });

      this.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        this.pointerOrigin = [event.clientX, event.clientY];
      }, { passive: true, signal });

      this.addEventListener('pointerup', (event) => this.finishGesture(event), { passive: true, signal });
      this.addEventListener('pointercancel', () => { this.pointerOrigin = null; }, { signal });
      this.addEventListener('mouseenter', () => this.hold(), { signal });
      this.addEventListener('mouseleave', () => this.arm(), { signal });
      this.addEventListener('focusin', () => this.hold(), { signal });
      this.addEventListener('focusout', (event) => {
        const focusStayedInside = event.relatedTarget && this.contains(event.relatedTarget);
        if (!focusStayedInside) this.arm();
      }, { signal });

      this.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.target.closest('input, select, textarea')) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.hold();
        else this.arm();
      }, { signal });

      const respondToEditor = (event) => {
        const selectedPanel = event.target.closest?.('[data-cuc-panel]');
        if (!selectedPanel || !this.contains(selectedPanel)) return;
        const editorSelectedBlock = event.type === 'shopify:block:select';
        this.pausedByUser = editorSelectedBlock;
        if (editorSelectedBlock) this.activate(this.panels.indexOf(selectedPanel), false);
        this.refreshPauseControl();
        if (!editorSelectedBlock) this.arm(true);
      };

      ['shopify:block:select', 'shopify:block:deselect'].forEach((editorEvent) => {
        document.addEventListener(editorEvent, respondToEditor, { signal });
      });

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.depthEnabled && !reduceMotion.matches) {
        const requestDepth = () => this.requestDepthUpdate();
        window.addEventListener('scroll', requestDepth, { passive: true, signal });
        window.addEventListener('resize', requestDepth, { passive: true, signal });
        requestDepth();
      }

      this.paint();
      this.arm();
    }

    disconnectedCallback() {
      this.cleanupController?.abort();
      this.cleanupController = null;
      window.clearTimeout(this.timeoutId);
      if (this.frameId) window.cancelAnimationFrame(this.frameId);
    }

    move(delta) {
      this.activate(this.activeIndex + delta, true);
    }

    activate(requestedIndex, restartCycle) {
      if (!this.panels.length) return;
      const count = this.panels.length;
      this.activeIndex = ((requestedIndex % count) + count) % count;
      this.paint();
      if (restartCycle) this.arm(true);
    }

    paint() {
      this.panels.forEach((panel, index) => {
        const isCurrent = index === this.activeIndex;
        panel.toggleAttribute('data-active', isCurrent);
        panel.setAttribute('aria-hidden', String(!isCurrent));
        if ('inert' in panel) panel.inert = !isCurrent;
      });

      this.selectors.forEach((selector, index) => {
        selector.setAttribute('aria-current', String(index === this.activeIndex));
      });

      this.requestDepthUpdate();
    }

    arm(force = false) {
      window.clearTimeout(this.timeoutId);
      this.classList.remove('is-running');

      const pointerIsOver = this.matches(':hover');
      const focusIsInside = this.contains(document.activeElement);
      if (!this.shouldCycle || this.pausedByUser || document.hidden || (!force && (pointerIsOver || focusIsInside))) return;

      void this.offsetWidth;
      this.classList.add('is-running');
      this.timeoutId = window.setTimeout(() => this.activate(this.activeIndex + 1, true), this.interval);
    }

    hold() {
      window.clearTimeout(this.timeoutId);
      this.classList.remove('is-running');
    }

    togglePlayback() {
      this.pausedByUser = !this.pausedByUser;
      this.refreshPauseControl();
      if (this.pausedByUser) this.hold();
      else this.arm(true);
    }

    refreshPauseControl() {
      if (!this.pauseControl) return;
      this.pauseControl.setAttribute('aria-pressed', String(this.pausedByUser));
      this.pauseControl.setAttribute('aria-label', this.pausedByUser ? 'Play slideshow' : 'Pause slideshow');
      this.pauseControl.textContent = this.pausedByUser ? 'Play slideshow' : 'Pause slideshow';
    }

    finishGesture(event) {
      if (!this.pointerOrigin || this.panels.length < 2) return;
      const horizontalTravel = event.clientX - this.pointerOrigin[0];
      const verticalTravel = event.clientY - this.pointerOrigin[1];
      this.pointerOrigin = null;
      if (Math.abs(horizontalTravel) < 45 || Math.abs(horizontalTravel) <= Math.abs(verticalTravel)) return;
      this.move(horizontalTravel < 0 ? 1 : -1);
    }

    requestDepthUpdate() {
      if (!this.depthEnabled || this.frameId) return;
      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = 0;
        const bounds = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight || 1;
        if (bounds.bottom <= 0 || bounds.top >= viewportHeight) return;
        const journey = (viewportHeight - bounds.top) / (viewportHeight + bounds.height);
        const displacement = (journey - 0.5) * 36;
        this.style.setProperty('--cuc-depth-y', `${displacement.toFixed(2)}px`);
      });
    }
  }

  customElements.define(elementName, CueroCampaignCarousel);
})();
